import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaCheck } from "react-icons/fa";
import { TbFilterPause } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { openModal } from "../../redux/features/modalSlice";
import Pagination from "../../common/Pagination";
import Search from "../../common/Search";
import NotificationFilters from "../../common/FilterTabs";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getUnreadCount,
} from "../../redux/services/notificatrion/notification.Services";
import { successToast, errorToast } from "../../Utils/toast";

function NotificationList() {
  const dispatch = useDispatch();

  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showActions, setShowActions] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    type: "",
    unread_only: "",
    from_date: "",
    to_date: "",
  });
  const [counts, setCounts] = useState({ all: 0, unread: 0, read: 0 });

  /* ---------------- RESPONSIVE ---------------- */
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) setShowActions(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------------- CLIENT FILTER ---------------- */
  const applyClientFilter = (data, filterType) => {
    if (filterType === "read") {
      setFilteredNotifications(data.filter((n) => !!n.read_at));
    } else if (filterType === "unread") {
      setFilteredNotifications(data.filter((n) => !n.read_at));
    } else {
      setFilteredNotifications(data);
    }
  };

  /* ---------------- FETCH ---------------- */
  const fetchNotifications = async (
    pageNo = 1,
    isInitialLoad = false,
    filterOverride = null
  ) => {
    const activeFilterLocal = filterOverride ?? activeFilter;
    try {
      if (isInitialLoad) setInitialLoading(true);
      else setTableLoading(true);

      const payload = {
        page: pageNo,
        per_page: limit,
        search: search || "",
        type: filters.type || "",
        from_date: filters.from_date || "",
        to_date: filters.to_date || "",
        unread_only: activeFilterLocal === "unread" ? 1 : 0,
      };

      const [listRes, countRes] = await Promise.all([
        getNotifications(payload),
        getUnreadCount({}),
      ]);

      const list =
        listRes?.data?.notifications ||
        listRes?.data ||
        listRes?.notifications ||
        [];
      const safeList = Array.isArray(list) ? list : [];

      setPage(listRes?.current_page || pageNo);
      setTotalPages(listRes?.last_page || 1);

      const unreadFromList =
        typeof listRes?.unread_count === "number" ? listRes.unread_count : null;
      const unreadFromCount = countRes?.data?.count ?? countRes?.count ?? null;
      const unread = unreadFromList ?? unreadFromCount ?? 0;
      setUnreadCount(unread);

      const filteredTotal = listRes?.total || safeList.length;
      setCounts((prev) => ({
        all: activeFilterLocal === "all" ? filteredTotal : prev.all,
        unread,
        read: activeFilterLocal === "read" ? filteredTotal : prev.read,
      }));

      applyClientFilter(safeList, activeFilterLocal);
    } catch (err) {
      console.log(err);
      errorToast("Failed to load notifications");
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    fetchNotifications(1, true, "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  /* ---------------- DEBOUNCED SEARCH (ONLY search) ---------------- */
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchNotifications(1, false, activeFilter);
    }, 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /* ---------------- PAGINATION ---------------- */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
      setPage(newPage);
      fetchNotifications(newPage, false, activeFilter);
    }
  };

  /* ---------------- FILTER TAB CLICK ---------------- */
  const handleFilterClick = (filterType) => {
    setActiveFilter(filterType);
    setPage(1);
    fetchNotifications(1, false, filterType); // pass filter directly
  };

  /* ---------------- MARK READ ---------------- */
  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead({ notification_id: id });
      fetchNotifications(page, false, activeFilter);
    } catch (err) {
      console.log("markRead error:", err?.response?.data);
      errorToast("Failed");
    }
  };

  /* ---------------- MARK ALL ---------------- */
  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead({});
      successToast("All marked as read");
      fetchNotifications(page, false, activeFilter);
    } catch (err) {
      console.log("markAll error:", err?.response?.data);
      errorToast("Failed");
    }
  };

  /* ---------------- FILTER FORM ---------------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchNotifications(1, false, activeFilter);
    successToast("Filters applied");
  };

  const handleResetFilters = () => {
    setFilters({ type: "", unread_only: "", from_date: "", to_date: "" });
    setSearch("");
    setActiveFilter("all");
    setPage(1);
    fetchNotifications(1, false, "all");
    successToast("Filters reset");
  };

  const handleToggleFilter = () => setShowFilter((p) => !p);

  /* ---------------- TABS ---------------- */
  const tabs = [
    {
      label: `All Notifications (${counts.all || 0})`,
      className: "btn-all",
      value: "all",
    },
    {
      label: `Unread (${counts.unread || 0})`,
      className: "btn-interested",
      value: "unread",
    },
    {
      label: `Read (${counts.read || 0})`,
      className: "btn-not-interested",
      value: "read",
    },
  ];

  if (initialLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      {/* ==================== HEADER ==================== */}
      <div className="pageheaderall position-relative d-md-flex align-items-center justify-content-between page-header-breadcrumb">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="page-header">
            <div>
              <h3 className="page-title">All Notifications</h3>
              <nav className="page-breadcrumb">
                <Link to="/">Home</Link>
                <span className="mx-2">/</span>
                <span>Notification List</span>
              </nav>
            </div>
          </div>

          {isMobile && (
            <button
              className="btn btn-primary"
              onClick={() => setShowActions(!showActions)}
            >
              <TbFilterPause />
            </button>
          )}
        </div>

        {/* Desktop Buttons */}
        {!isMobile && (
          <div className="d-flex align-items-center w-100 gap-2 justify-content-end">
            <div style={{ minWidth: 240 }}>
              <Search
                search={search}
                onSearch={setSearch}
                placeholder="Search notifications..."
              />
            </div>

            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`lead-btn btn-all ${tab.className} ${
                  activeFilter === tab.value ? "active" : ""
                }`}
                onClick={() => handleFilterClick(tab.value)}
              >
                {tab.label}
              </button>
            ))}

            <button
              className="lead-btn btn-filter"
              onClick={handleToggleFilter}
            >
              <i className="fas fa-filter me-1"></i>
              {showFilter ? "Hide Filter" : "Filter"}
            </button>

            {unreadCount > 0 && (
              <button className="lead-btn btn-add" onClick={handleMarkAll}>
                Mark All Read
              </button>
            )}
          </div>
        )}
      </div>

      {/* ==================== MOBILE BOTTOM SHEET ==================== */}
      {isMobile && (
        <>
          <div
            className={`menu-overlay ${showActions ? "show" : ""}`}
            onClick={() => setShowActions(false)}
          ></div>

          <div
            className={`dorpdownloaddatabutton ${
              showActions ? "active" : ""
            }`}
          >
            <div style={{ padding: "0 4px 12px" }}>
              <Search
                search={search}
                onSearch={setSearch}
                placeholder="Search notifications..."
              />
            </div>

            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`lead-btn btn-all ${tab.className} ${
                  activeFilter === tab.value ? "active" : ""
                }`}
                onClick={() => {
                  handleFilterClick(tab.value);
                  setShowActions(false);
                }}
              >
                {tab.label}
              </button>
            ))}

            <button
              className="lead-btn btn-filter"
              onClick={() => {
                handleToggleFilter();
                setShowActions(false);
              }}
            >
              <i className="fas fa-filter me-1"></i>
              {showFilter ? "Hide Filter" : "Filter"}
            </button>

            {unreadCount > 0 && (
              <button
                className="lead-btn btn-add"
                onClick={() => {
                  handleMarkAll();
                  setShowActions(false);
                }}
              >
                Mark All Read
              </button>
            )}
          </div>
        </>
      )}

      {/* ==================== FILTER FORM ==================== */}
      <NotificationFilters
        showFilter={showFilter}
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        onToggleFilter={handleToggleFilter}
      />

      {/* ==================== MAIN ==================== */}
      <div className="main_page">
        {totalPages > 1 && (
          <div className="d-flex justify-content-end mb-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <div className="st-wrapper-card text-center">
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr.No.</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary"></div>
                    </td>
                  </tr>
                ) : filteredNotifications.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      No notifications found
                    </td>
                  </tr>
                ) : (
                  filteredNotifications.map((item, index) => {
                    const id = item.id;
                    return (
                      <tr key={id}>
                        <td>{(page - 1) * limit + index + 1}</td>
                        <td className="fw-semibold">
                          {item.title || "Notification"}
                        </td>
                        <td
                          style={{
                            maxWidth: 320,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={item.message}
                        >
                          {item.message || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {item.created_at
                            ? new Date(item.created_at).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`crm-status-badge ${
                              item.read_at
                                ? "crm-status-completed"
                                : "crm-status-busy"
                            }`}
                          >
                            {item.read_at ? "Read" : "Unread"}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <button
                              className="st-action-btn st-view-btn"
                              title="View"
                              onClick={() =>
                                dispatch(
                                  openModal({
                                    type: "view-notification",
                                    title: "View Notification",
                                    data: item,
                                  })
                                )
                              }
                            >
                              <FaEye />
                            </button>
                            {!item.read_at && (
                              <button
                                className="st-action-btn st-edit-btn"
                                title="Mark as read"
                                onClick={() => handleMarkRead(id)}
                              >
                                <FaCheck />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-end mt-3">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationList;